import {
  wrapLogBold, wrapLogDept, wrapLogPrefTag, wrapLogRaw, stripLogFormatFromUserInput,
} from './logFormat';
import { InventoryItem } from 'pages/redux';
import { Log } from './hooks';
import { ERP_PATHS } from 'pages/Layout/erpModulePaths';
import { getLogActorIdentity, resolveActorDisplayName, StaffMaps } from './logActor';
import {
  ActivityGroupsModel,
  activityLogsCheckedToRevealLogSelection,
  activityLogsTableGroupTypeName,
  isActivityGroupUuid,
  isActivitySkuSelectionId,
  logMatchesActivityGroupsSelection,
  SHOW_ALL_ACTIVITY_LOGS,
} from './activityGroupsModel';

/** Matches manual activity description limit in ActivityForm. Keep in sync with LOG_DETAILS_MAX. */
export const BRANCH_FILE_DETAILS_MAX = 400;

/** Punctuation unsafe inside activity_logs.message token segments. */
export const LOG_UNSAFE_CHARS_RE = /[`!@#$%^&*()_+\-=[\]{};':"\\|<>/?~]/;
export const LOG_UNSAFE_NAME_CHARS_RE = /[`!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?~]/;
export const LOG_UNSAFE_CHARS_TOAST = 'Special characters are not allowed';

export const containsLogUnsafeChars = (
  value: string,
  mode: 'text' | 'name' = 'text',
): boolean => (
  (mode === 'name' ? LOG_UNSAFE_NAME_CHARS_RE : LOG_UNSAFE_CHARS_RE).test(value)
);

/** Open workflow work: activity_logs with no start date. Dated rows are completed activity. */
export const isWorkflowTaskLog = (log: { date?: string | Date | null }): boolean => {
  if (log.date == null || log.date === '') return true;
  const t = +new Date(log.date);
  return !Number.isFinite(t);
};

/**
 * Soft cap for activity_logs.message (column/worker addLog).
 * Keep in sync with `LOG_MESSAGE_MAX` in lodgefirst_column_v1/routes/logs.ts.
 */
export const LOG_MESSAGE_MAX = 1600;
/** Cap for desc inner text; keep in sync with column `LOG_DETAILS_MAX`. */
export const LOG_DETAILS_MAX = 400;

/** Department cash records that are not inventory sales (activityLogs selector group). */
export const DEPARTMENT_ENTRY_GROUP = 'Department entries';
export const DEPARTMENT_ENTRY_TYPES = ['Direct', 'Expense', 'Document'] as const;
export type DepartmentEntryType = typeof DEPARTMENT_ENTRY_TYPES[number];

export const isDepartmentEntryType = (type?: string | null): boolean => (
  Boolean(type && (DEPARTMENT_ENTRY_TYPES as readonly string[]).includes(type))
);

/** Inventory names are not bracket-wrapped. Keep in sync with column. */
export const wrapLogInventoryItemName = (name: string): string => (
  // Strip format marks even for unwrapped names that sit next to wraps in the message.
  stripLogFormatFromUserInput(String(name || '').trim())
);

/** Bold inventory item name via wrapLogBold. */
export const inventoryItemLogToken = (name: string): string => (
  wrapLogBold(String(name || '').trim())
);

/** Refund tag: bold `refund` inside brackets. */
export const REFUND_LOG_TOKEN = wrapLogPrefTag('refund');

/** Sale / room-charge details: `&Heineken& ×2`. Group lives on inventory_type, not the message. */
export const formatLogInventoryItemDetails = (
  name: string,
  quantity?: string | number | null,
): string => {
  const token = inventoryItemLogToken(name);
  if (quantity == null || quantity === '') return token;
  return `${token} ×${quantity}`;
};

/**
 * Split activity_logs.message into display tokens.
 * Includes `&G&` (customer). Do not include `_` / `#` — customer ids like `c_chi`
 * must remain a single token.
 */
export const LOG_MESSAGE_SPLIT_RE = /(?:\||&P&|&V&|&-&|&C&|&A&|&L&|&R&|&G&|\^|!|&)+/;

export const MB = 1024 * 1024;
export const GB = 1024 * MB;

/** Keep in sync with be and worker */
export const DEFAULT_ORGANIZATION_STORAGE_MAX_BYTES = 20 * MB;
export const DEFAULT_DB_STORAGE_MAX_BYTES = 10 * MB;
export const DEFAULT_OTHERS_STORAGE_MAX_BYTES = DEFAULT_ORGANIZATION_STORAGE_MAX_BYTES - DEFAULT_DB_STORAGE_MAX_BYTES;
/** Storage included automatically with any active user seats plan. */
export const SEATS_INCLUDED_STORAGE_BYTES = 5 * GB;
/** Warn when remaining default DB capacity is under this (no seats/storage plan). */
export const STORAGE_WARN_HEADROOM_BYTES_DEFAULT_DB = 3 * MB;
/** Warn when remaining default photo/file capacity is under this (no seats/storage plan). */
export const STORAGE_WARN_HEADROOM_BYTES_DEFAULT_OTHERS = 1 * MB;
/** Warn when remaining capacity is under this (has seats/storage entitlement). */
export const STORAGE_WARN_HEADROOM_BYTES_PLAN = 2 * MB;
export const LOG_ENTRY_BYTES = 4 * 1024;
export const PHOTO_LIMIT_BYTES = 3 * MB;
export const PHOTO_ASSUMED_RATIO = 0.6;
export const PHOTO_ASSUMED_BYTES = Math.floor(PHOTO_LIMIT_BYTES * PHOTO_ASSUMED_RATIO);

/**
 * Organization-scoped DB row weights. Keep in sync with column/worker `organizationStorage.ts`.
 * Skip platform tables (app_settings, emails_sent, organizations_tmp, unsubscribe,
 * paid_to_me, no_verify_*, transactions, webhook_fail_pay_me).
 */
export const DB_ROW_WEIGHTS = {
  logs: LOG_ENTRY_BYTES,
  customers: 2 * 1024,
  inventory: 1024,
  rooms: 4 * 1024,
  staff: 1024,
  billing: 512,
  feedback: 1024,
  config: 8 * 1024,
  organizations: 8 * 1024,
  photos: 512,
  managerReports: 2 * 1024,
  reportRequests: 512,
  /** Structural DAG — activity_scopes / links / linked_skus(+items). */
  activityGroups: 512,
  activityGroupLinks: 128,
  activityGroupLinkedSkus: 256,
  activityGroupLinkedSkuItems: 64,
  /** Structural — company departments / workflow graphs. */
  companyDepartments: 512,
  workflows: 4 * 1024,
  erpRecords: 1024,
  employeeInvites: 512,
  staffRecordGrants: 256,
  erpDirectives: 2 * 1024,
} as const;

/** Shorten CSV/Excel Description cells used as wrapLogDesc in branch-file logs. */
export const shortenBranchFileDetails = (value: string): string => (
  stripLogFormatFromUserInput(value)
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, BRANCH_FILE_DETAILS_MAX)
);

/** Parse Paystack title e.g. "Storage (0.2GB)". */
export const parseStoragePlanBytes = (title: string): number => {
  const m = String(title || '').match(/(\d+(?:\.\d+)?)\s*(GB|MB|KB)/i);
  if (!m) return 0;
  const n = Number(m[1]);
  if (!Number.isFinite(n) || n <= 0) return 0;
  const unit = m[2].toUpperCase();
  if (unit === 'GB') return Math.floor(n * GB);
  if (unit === 'MB') return Math.floor(n * MB);
  return Math.floor(n * 1024);
};

export type OrganizationStorageFields = {
  maxStorage?: number | null,
  storagePlan?: string | null,
  storageSubscription?: { planTitle?: string } | null,
  /** Active seats plan also unlocks SEATS_INCLUDED_STORAGE_BYTES. */
  seatsPlan?: string | null,
  maxLogs?: number | null,
  logsPlan?: string | null,
  logsSubscription?: { planTitle?: string } | null,
};

/** True when property has expanded storage (legacy storage plan or seats plan with 5GB). */
export const hasStoragePlan = (hotel?: OrganizationStorageFields | null): boolean => {
  if (!hotel) return false;
  return Boolean(hotel.storagePlan || hotel.logsPlan || hotel.seatsPlan);
};

export const resolveStorageWarnHeadroomBytes = (
  hotel?: OrganizationStorageFields | null,
): number => (
  hasStoragePlan(hotel)
    ? STORAGE_WARN_HEADROOM_BYTES_PLAN
    : STORAGE_WARN_HEADROOM_BYTES_DEFAULT_DB
);

export const resolveOthersStorageWarnHeadroomBytes = (
  hotel?: OrganizationStorageFields | null,
): number => (
  hasStoragePlan(hotel)
    ? STORAGE_WARN_HEADROOM_BYTES_PLAN
    : STORAGE_WARN_HEADROOM_BYTES_DEFAULT_OTHERS
);

/** Paid storage-plan bytes only (does not include seats 5GB). */
export const resolvePaidStoragePlanBytes = (hotel?: OrganizationStorageFields | null): number => {
  if (!hotel) return 0;
  const title = hotel.storageSubscription?.planTitle
    || hotel.logsSubscription?.planTitle
    || '';
  const fromTitle = parseStoragePlanBytes(title);
  if (fromTitle > 0) return fromTitle;

  // Title missing — infer paid slice from stored maxStorage minus seats 5GB
  if (!(hotel.storagePlan || hotel.logsPlan)) return 0;
  const fromField = Number(hotel.maxStorage);
  if (!(Number.isFinite(fromField) && fromField > 0)) return 0;
  const seatsBytes = hotel.seatsPlan ? SEATS_INCLUDED_STORAGE_BYTES : 0;
  if (seatsBytes > 0 && fromField > seatsBytes) return Math.floor(fromField - seatsBytes);
  if (!seatsBytes) return Math.floor(fromField);
  return 0;
};

/**
 * Overall entitlement = seats-included 5GB + subscribed storage plan (additive).
 * Keep in sync with BE organizationStorage.resolveMaxStorageBytes.
 */
export const resolveMaxStorageBytes = (hotel?: OrganizationStorageFields | null): number | null => {
  if (!hotel) return null;

  const seatsBytes = hotel.seatsPlan ? SEATS_INCLUDED_STORAGE_BYTES : 0;
  const paidBytes = resolvePaidStoragePlanBytes(hotel);
  if (seatsBytes > 0 || paidBytes > 0) return seatsBytes + paidBytes;

  const fromField = Number(hotel.maxStorage);
  if (Number.isFinite(fromField) && fromField > 0) return Math.floor(fromField);

  const legacy = Number(hotel.maxLogs);
  if (Number.isFinite(legacy) && legacy > 0) {
    return Math.max(50 * MB, Math.floor(legacy / 200) * MB);
  }
  return null;
};

/** Recompute maxStorage after seats/storage subscribe or cancel. */
export const computeEntitledStorageMaxBytes = (opts: {
  hasSeatsPlan?: boolean
  storagePlanTitle?: string | null
}): number | null => {
  const seatsBytes = opts.hasSeatsPlan ? SEATS_INCLUDED_STORAGE_BYTES : 0;
  const paidBytes = parseStoragePlanBytes(String(opts.storagePlanTitle || ''));
  const sum = seatsBytes + paidBytes;
  return sum > 0 ? sum : null;
};

export const resolveOrganizationStorageMaxBytes = (hotel?: OrganizationStorageFields | null): number => {
  if (hasStoragePlan(hotel)) {
    const n = resolveMaxStorageBytes(hotel);
    if (n && n > 0) return n;
  }
  return DEFAULT_ORGANIZATION_STORAGE_MAX_BYTES;
};

export const resolveDbStorageMaxBytes = (hotel?: OrganizationStorageFields | null): number => (
  hasStoragePlan(hotel) ? resolveOrganizationStorageMaxBytes(hotel) : DEFAULT_DB_STORAGE_MAX_BYTES
);

export const resolveOthersStorageMaxBytes = (hotel?: OrganizationStorageFields | null): number => (
  hasStoragePlan(hotel) ? resolveOrganizationStorageMaxBytes(hotel) : DEFAULT_OTHERS_STORAGE_MAX_BYTES
);

export const formatStorageBytes = (bytes: number): string => {
  const n = Math.max(0, Number(bytes) || 0);
  const mb = n / MB;
  if (mb >= 1024) {
    const gb = mb / 1024;
    if (gb >= 10) return `${Math.round(gb)}GB`;
    return `${(Math.round(gb * 10) / 10).toString().replace(/\.0$/, '')}GB`;
  }
  if (mb >= 10) return `${Math.round(mb)}MB`;
  if (mb >= 1) return `${(Math.round(mb * 10) / 10).toString().replace(/\.0$/, '')}MB`;
  const kb = n / 1024;
  if (kb >= 1) return `${Math.round(kb)}KB`;
  return `${Math.round(n)}B`;
};

/** @deprecated use formatStorageBytes */
export const formatBranchFilesBytes = formatStorageBytes;

/** Estimate stored JSON size of one branch sheet (mirrors GCP object body). */
export const estimateBranchSheetBytes = (sheet: unknown): number => {
  try {
    return new Blob([JSON.stringify(sheet ?? [])]).size;
  } catch {
    return 0;
  }
};

/** Estimate total GCP branch-file size from local branchFiles mirror. */
export const estimateBranchFilesBytes = (
  branchFiles?: Record<string, Record<string, unknown>> | null,
): number => {
  if (!branchFiles) return 0;
  let total = 0;
  Object.values(branchFiles).forEach((sheets) => {
    Object.values(sheets || {}).forEach((sheet) => {
      total += estimateBranchSheetBytes(sheet);
    });
  });
  return total;
};

export const estimateDbBytesFromCounts = (counts: {
  logs?: number,
  customers?: number,
  inventory?: number,
  rooms?: number,
  staff?: number,
  billing?: number,
  feedback?: number,
  config?: number,
  organizations?: number,
  photos?: number,
  managerReports?: number,
  reportRequests?: number,
  activityGroups?: number,
  activityGroupLinks?: number,
  activityGroupLinkedSkus?: number,
  activityGroupLinkedSkuItems?: number,
  companyDepartments?: number,
  workflows?: number,
  erpRecords?: number,
  employeeInvites?: number,
  staffRecordGrants?: number,
  erpDirectives?: number,
}): number => (
  (Number(counts.logs) || 0) * DB_ROW_WEIGHTS.logs
  + (Number(counts.customers) || 0) * DB_ROW_WEIGHTS.customers
  + (Number(counts.inventory) || 0) * DB_ROW_WEIGHTS.inventory
  + (Number(counts.rooms) || 0) * DB_ROW_WEIGHTS.rooms
  + (Number(counts.staff) || 0) * DB_ROW_WEIGHTS.staff
  + (Number(counts.billing) || 0) * DB_ROW_WEIGHTS.billing
  + (Number(counts.feedback) || 0) * DB_ROW_WEIGHTS.feedback
  + (Number(counts.config) || 0) * DB_ROW_WEIGHTS.config
  + (Number(counts.organizations) || 0) * DB_ROW_WEIGHTS.organizations
  + (Number(counts.photos) || 0) * DB_ROW_WEIGHTS.photos
  + (Number(counts.managerReports) || 0) * DB_ROW_WEIGHTS.managerReports
  + (Number(counts.reportRequests) || 0) * DB_ROW_WEIGHTS.reportRequests
  + (Number(counts.activityGroups) || 0) * DB_ROW_WEIGHTS.activityGroups
  + (Number(counts.activityGroupLinks) || 0) * DB_ROW_WEIGHTS.activityGroupLinks
  + (Number(counts.activityGroupLinkedSkus) || 0) * DB_ROW_WEIGHTS.activityGroupLinkedSkus
  + (Number(counts.activityGroupLinkedSkuItems) || 0) * DB_ROW_WEIGHTS.activityGroupLinkedSkuItems
  + (Number(counts.companyDepartments) || 0) * DB_ROW_WEIGHTS.companyDepartments
  + (Number(counts.workflows) || 0) * DB_ROW_WEIGHTS.workflows
  + (Number(counts.erpRecords) || 0) * DB_ROW_WEIGHTS.erpRecords
  + (Number(counts.employeeInvites) || 0) * DB_ROW_WEIGHTS.employeeInvites
  + (Number(counts.staffRecordGrants) || 0) * DB_ROW_WEIGHTS.staffRecordGrants
  + (Number(counts.erpDirectives) || 0) * DB_ROW_WEIGHTS.erpDirectives
);

export const estimatePhotoBytes = (photoCount: number): number => (
  Math.max(0, Number(photoCount) || 0) * PHOTO_ASSUMED_BYTES
);

/** Count photo-using items (inventory images, staff photos). */
export const countPhotoUsingItems = (parts: {
  inventory?: Array<{ image?: string | null }> | null,
  users?: Array<{ photo?: string | null }> | null,
}): number => {
  let n = 0;
  (parts.inventory || []).forEach((item) => {
    if (item?.image) n += 1;
  });
  (parts.users || []).forEach((u) => {
    if (u?.photo) n += 1;
  });
  return n;
};

export const OTHERS_STORAGE_QUOTA_HINT =
  'Delete unused branch tables or photos, or add storage / user seats (seats include 5GB; storage plans add on top).';

export const DB_STORAGE_QUOTA_HINT =
  'Delete unused data or add storage / user seats (seats include 5GB; storage plans add on top). New saves are blocked while storage is full.';

export const ROOMS_STORAGE_QUOTA_HINT =
  'Subscribe to user seats (includes 5GB) or a storage plan to add more rooms.';

export const INVENTORY_STORAGE_QUOTA_HINT =
  'Subscribe to user seats (includes 5GB) or a storage plan to add more inventory items.';

/** Whether adding estimated DB bytes would exceed storage (hotel max with plan, else DB max). */
export const wouldExceedStorageForDbAdd = (
  hotel: OrganizationStorageFields | null | undefined,
  used: { dbBytes: number, totalBytes: number },
  addBytes: number,
): { over: boolean, shownUsed: number, shownLimit: number, addBytes: number } => {
  const extra = Math.max(0, Number(addBytes) || 0);
  if (hasStoragePlan(hotel)) {
    const shownUsed = used.totalBytes + extra;
    const shownLimit = resolveOrganizationStorageMaxBytes(hotel);
    return { over: shownUsed > shownLimit, shownUsed, shownLimit, addBytes: extra };
  }
  const shownUsed = used.dbBytes + extra;
  const shownLimit = resolveDbStorageMaxBytes(hotel);
  return { over: shownUsed > shownLimit, shownUsed, shownLimit, addBytes: extra };
};

/** Whether adding photo/file bytes would exceed storage (hotel max with plan, else others max). */
export const wouldExceedStorageForOthersAdd = (
  hotel: OrganizationStorageFields | null | undefined,
  used: {
    branchBytes: number,
    photoBytes: number,
    workOrderSecretFileBytes?: number,
    totalBytes: number,
  },
  addBytes: number,
): { over: boolean, shownUsed: number, shownLimit: number, addBytes: number } => {
  const extra = Math.max(0, Number(addBytes) || 0);
  if (hasStoragePlan(hotel)) {
    const shownUsed = used.totalBytes + extra;
    const shownLimit = resolveOrganizationStorageMaxBytes(hotel);
    return { over: shownUsed > shownLimit, shownUsed, shownLimit, addBytes: extra };
  }
  const othersUsed = used.branchBytes + used.photoBytes + (used.workOrderSecretFileBytes || 0);
  const shownUsed = othersUsed + extra;
  const shownLimit = resolveOthersStorageMaxBytes(hotel);
  return { over: shownUsed > shownLimit, shownUsed, shownLimit, addBytes: extra };
};

/** Whether adding `addRoomCount` rooms would exceed storage. */
export const wouldExceedStorageForRooms = (
  hotel: OrganizationStorageFields | null | undefined,
  used: { dbBytes: number, totalBytes: number },
  addRoomCount: number,
) => wouldExceedStorageForDbAdd(
  hotel,
  used,
  Math.max(0, Number(addRoomCount) || 0) * DB_ROW_WEIGHTS.rooms,
);

/** Whether adding `addInventoryCount` inventory rows would exceed storage. */
export const wouldExceedStorageForInventory = (
  hotel: OrganizationStorageFields | null | undefined,
  used: { dbBytes: number, totalBytes: number },
  addInventoryCount: number,
) => wouldExceedStorageForDbAdd(
  hotel,
  used,
  Math.max(0, Number(addInventoryCount) || 0) * DB_ROW_WEIGHTS.inventory,
);

/** @deprecated use OTHERS_STORAGE_QUOTA_HINT */
export const BRANCH_FILES_QUOTA_HINT = OTHERS_STORAGE_QUOTA_HINT;

/** Fixed inventory lifecycle activity types (listed under Actions). */
export const INVENTORY_SYSTEM_TYPES = [
  'Inventory Added',
  'Inventory Renamed',
  'Inventory deleted',
] as const;

export type InventorySystemType = typeof INVENTORY_SYSTEM_TYPES[number];

/** Legacy hospitality / CRM activity types — omit from /logs Actions (old rows may still exist). */
const LEGACY_HIDDEN_ACTIVITY_LOG_TYPES = new Set([
  'Desk reservation',
  'Online reservation',
  'Reservation cancelled',
  'Reservation change',
  'Check in',
  'Desk check-in',
  'Check out',
  'Meal delivered',
  'Housekeeping',
  'Room Actions',
  'Key card access',
  'Room charge',
  'Walk in',
  'Walk out',
  'Rate Plan change',
  'Room added',
  'Room change',
  'Room put on hold',
  'Room hold removed',
  'Room renamed',
  'Room floor changed',
  'Room perks changed',
  'Room type changed',
  'Room deleted',
  'CRM campaign added',
  'CRM campaign removed',
  'CRM campaign activated',
  'CRM campaign deactivated',
  'CRM campaign changed',
  'CRM campaign graphic uploaded',
  'CRM campaign graphic deleted',
  'CRM discount changed',
  'CRM email routing changed',
  'CRM settings change',
]);

/** Types that must not appear under /logs Actions or in the table. */
export const isHiddenActivityLogsActionType = (type: string) => (
  LEGACY_HIDDEN_ACTIVITY_LOG_TYPES.has(type)
);

export type LogMatchable = {
  id?: string | number,
  type: string,
  /** Group type string (same as type when API maps group_type → both). Activity scope in the UI. */
  groupType?: string | null,
  department?: string | null,
  /** Plural department field from API. */
  departments?: string | null,
  inventory_type?: string | null,
  units?: string | number | null,
  inventory_item_id?: string | null,
  message?: string,
  created_by?: string | null,
  eventType?: string | null,
  action_data_id?: string | null,
  actionDataId?: string | null,
  skus?: Array<{ id?: string | null, name?: string | null }> | null,
  relatedGroups?: { id: string, kind?: string }[] | null,
};

/** Former generic "Room change" type — show those rows under any of the new per-field actions. */
const ROOM_EDIT_ACTIVITY_TYPES = [
  'room put on hold', 'room hold removed', 'room renamed',
  'room floor changed', 'room perks changed', 'room type changed',
];

/** Whether a log belongs to a selected activity name (type / department / inventory_type). */
export const logMatchesActivity = (activity: string, log: LogMatchable): boolean => {
  if (!activity) return false;
  const a = activity.toLowerCase();
  if (log.type?.toLowerCase() === a) return true;
  if (log.type?.toLowerCase() === 'room change' && ROOM_EDIT_ACTIVITY_TYPES.includes(a)) {
    return true;
  }
  // Direct / Expense / Document match their type only — not via department.
  // Metrics Active activityLogs: a selected department must show that department's inventory.
  if (!isDepartmentEntryType(log.type) && log.department?.toLowerCase() === a) return true;
  if (log.inventory_type?.toLowerCase() === a) return true;
  return false;
};

const ROOM_EDIT_ACTIVITY_TYPE_SET = new Set(ROOM_EDIT_ACTIVITY_TYPES);

/** O(1) per log vs O(activityLogs) `some(logMatchesActivity)` — same match rules. */
const logMatchesActivitySet = (activitySet: Set<string>, log: LogMatchable): boolean => {
  const type = log.type?.toLowerCase();
  if (type && activitySet.has(type)) return true;
  if (type === 'room change') {
    for (const roomEditType of ROOM_EDIT_ACTIVITY_TYPE_SET) {
      if (activitySet.has(roomEditType)) return true;
    }
  }
  if (!isDepartmentEntryType(log.type)) {
    const department = log.department?.toLowerCase();
    if (department && activitySet.has(department)) return true;
  }
  const inventoryType = log.inventory_type?.toLowerCase();
  if (inventoryType && activitySet.has(inventoryType)) return true;
  return false;
};

/** Deduplicate logs by id while preserving order. */
export const dedupeLogsById = <T extends { id?: string | number }>(logs: T[]): T[] => {
  const seen = new Set<string>();
  const out: T[] = [];
  logs.forEach((log) => {
    const key = log.id == null ? `anon_${out.length}` : String(log.id);
    if (seen.has(key)) return;
    seen.add(key);
    out.push(log);
  });
  return out;
};

/**
 * Filter logs that match any of the selected activityLogs, once per id.
 * Metrics charts keep this OR matcher so department totals still include inventory sales.
 * Department entries (Direct / Expense / Document) match their type only, so a
 * selected department on Active activityLogs shows inventory rather than those entries.
 * The /logs table uses filterLogsByActivityLogsTable (department/group scope inventory when selected).
 */
export const filterLogsByActivityLogs = <T extends LogMatchable>(
  logs: T[],
  activityLogs: string[],
): T[] => {
  if (!activityLogs?.length) return [];
  const activitySet = new Set<string>();
  for (let i = 0; i < activityLogs.length; i += 1) {
    const name = activityLogs[i];
    if (name) activitySet.add(name.toLowerCase());
  }
  if (!activitySet.size) return [];
  const matched = logs.filter((log) => logMatchesActivitySet(activitySet, log));
  return dedupeLogsById(matched);
};

export const isInventorySystemType = (type: string) => (
  (INVENTORY_SYSTEM_TYPES as readonly string[]).includes(type)
);

/** Alphabetical order for Inventory / Inventory Group activity labels. */
export const sortActivityLabels = (labels: string[]): string[] => (
  [...labels].sort((a, b) => a.localeCompare(b, undefined, {
    sensitivity: 'base',
    numeric: true,
  }))
);

/** Inventory Group activityLogs: manually added inventory groups (types). */
export const buildInventoryGroupActivityLogs = ({
  inventoryTypes,
  inventory,
  logs,
}: {
  inventoryTypes?: string[] | null,
  inventory?: InventoryItem[] | null,
  logs?: Log[] | null,
}): string[] => {
  const types = new Map<string, string>();

  const addType = (raw?: string | null) => {
    const trimmed = raw?.trim();
    if (!trimmed || isInventorySystemType(trimmed)) return;
    const key = trimmed.toLowerCase();
    if (!types.has(key)) types.set(key, trimmed);
  };

  (inventoryTypes || []).forEach((t) => addType(t));
  (inventory || []).forEach((item) => addType(item?.type));
  (logs || []).forEach((log) => addType(log.inventory_type));

  return sortActivityLabels(Array.from(types.values()));
};

/** Inventory activityLogs: inventory item names only. */
export const buildInventoryItemActivityLogs = ({
  inventory,
  logs,
  inventoryGroupNames,
}: {
  inventory?: InventoryItem[] | null,
  logs?: Log[] | null,
  inventoryGroupNames?: string[] | null,
}): string[] => {
  const groupSet = new Set(
    (inventoryGroupNames || []).map((t) => t.trim().toLowerCase()).filter(Boolean),
  );
  const items = new Map<string, string>();

  const addItem = (raw?: string | null) => {
    const trimmed = raw?.trim();
    if (!trimmed || groupSet.has(trimmed.toLowerCase())) return;
    const key = trimmed.toLowerCase();
    if (!items.has(key)) items.set(key, trimmed);
  };

  (inventory || []).forEach((item) => addItem(item?.name));

  (logs || []).forEach((log) => {
    if (isInventorySystemType(log.type)) return;
    if (isDepartmentEntryType(log.type)) return;
    // Item sales / item activityLogs use the activity (type) field as the item name.
    if (log.inventory_item_id || log.units != null || log.inventory_type) {
      addItem(log.type);
    }
  });

  return sortActivityLabels(Array.from(items.values()));
};

/** @deprecated Prefer buildInventoryGroupActivityLogs + buildInventoryItemActivityLogs. */
export const buildInventoryActivityTypes = ({
  inventoryTypes,
  inventory,
  logs,
}: {
  inventoryTypes?: string[] | null,
  inventory?: InventoryItem[] | null,
  logs?: Log[] | null,
}): string[] => {
  const groups = buildInventoryGroupActivityLogs({ inventoryTypes, inventory, logs });
  const items = buildInventoryItemActivityLogs({
    inventory,
    logs,
    inventoryGroupNames: groups,
  });
  return [...INVENTORY_SYSTEM_TYPES, ...groups, ...items];
};

/** Departments present via branch names or the departments field on logs. */
export const departmentsPresentInLogs = (
  logs: Log[] | null | undefined,
  departmentNames: string[],
): string[] => {
  const present = new Set<string>();
  (logs || []).forEach((log) => {
    const dept = (log.departments || log.department || '').trim();
    if (dept) present.add(dept);
    // Legacy department activityLogs used type = department name.
    if (!dept && departmentNames.includes(log.type)) {
      present.add(log.type);
    }
  });
  return departmentNames.filter((name) => present.has(name));
};

/** User-facing warning for rename confirmations based on how many logs will update. */
export const renameLogsWarning = (
  matchCount: number,
  logsLoaded = true,
): string => {
  if (!logsLoaded) {
    return 'Past logs that use this name will also be updated. Depending on how many logs you have, this may take a while — please keep this window open until it finishes.';
  }
  if (matchCount <= 0) {
    return 'Past logs that use this name will also be updated if any are found. This is usually quick.';
  }
  if (matchCount < 50) {
    return `This also updates ${matchCount} past log ${matchCount === 1 ? 'entry' : 'entries'}.`;
  }
  if (matchCount < 500) {
    return `This also updates about ${matchCount} past log entries and may take a few seconds. Please keep this window open.`;
  }
  return `This also updates about ${matchCount} past log entries. With this many logs it may take a while — please keep this window open until it finishes.`;
};

export const rewriteDepartmentNameInMessage = (
  message: string,
  from: string,
  to: string,
): string => {
  if (!message || !from || from === to) return message;
  // Match the token as stored (raw); write the new name through wrapLogDept (strips marks).
  return message
    .split(wrapLogRaw('dept', from)).join(wrapLogDept(to))
    .split(`!${from}!`).join(`!${to}!`);
};

export const countLogsForDepartmentRename = (
  logs: Array<LogMatchable & { message?: string }> | null | undefined,
  from: string,
): number => {
  if (!logs?.length || !from) return 0;
  let count = 0;
  logs.forEach((log) => {
    const dept = log.department != null ? String(log.department) : '';
    const logType = log.type != null ? String(log.type) : '';
    const msg = typeof log.message === 'string' ? log.message : '';
    const hasInventorySignal = Boolean(
      log.inventory_type
      || log.inventory_item_id
      || (log.units != null && log.units !== ''),
    );
    if (
      dept === from
      || msg.includes(wrapLogRaw('dept', from))
      || msg.includes(`!${from}!`)
      || (logType === from && !hasInventorySignal)
    ) {
      count += 1;
    }
  });
  return count;
};

export const rewriteLogsForDepartmentRename = <T extends LogMatchable & { message?: string }>(
  logs: T[] | null | undefined,
  from: string,
  to: string,
): T[] | null => {
  if (!logs) return null;
  if (!from || !to || from === to) return logs;
  return logs.map((log) => {
    const dept = log.department != null ? String(log.department) : '';
    const logType = log.type != null ? String(log.type) : '';
    const msg = typeof log.message === 'string' ? log.message : '';
    const hasInventorySignal = Boolean(
      log.inventory_type
      || log.inventory_item_id
      || (log.units != null && log.units !== ''),
    );
    const matches = dept === from
      || msg.includes(wrapLogRaw('dept', from))
      || msg.includes(`!${from}!`)
      || (logType === from && !hasInventorySignal);
    if (!matches) return log;
    const next: T = {
      ...log,
      message: rewriteDepartmentNameInMessage(msg, from, to),
    };
    if (dept === from) next.department = to;
    if (logType === from && !hasInventorySignal) next.type = to;
    return next;
  });
};

/**
 * Primary log group for charts / all-balances aggregation.
 * Inventory item sales belong to Inventory (not Departments), even with a department field.
 */
export const getPrimaryLogGroup = (
  log: LogMatchable,
  logGroups: Record<string, string[]>,
): string => {
  if (isInventorySystemType(log.type)) return 'Actions';
  if (isDepartmentEntryType(log.type)) return DEPARTMENT_ENTRY_GROUP;

  const isInventoryItem = Boolean(log.inventory_item_id)
    || (log.units != null && Boolean(log.inventory_type))
    || (logGroups.Inventory || []).includes(log.type);
  if (isInventoryItem) return 'Inventory';

  const invGroup = logGroups['Inventory Group'] || [];
  if (invGroup.includes(log.type)
    || Boolean(log.inventory_type && invGroup.includes(log.inventory_type))) {
    return 'Inventory Group';
  }

  if (log.department && (logGroups.Departments || []).includes(log.department)) {
    return 'Departments';
  }
  if ((logGroups.Departments || []).includes(log.type)) return 'Departments';
  if ((logGroups.Actions || []).includes(log.type)) return 'Actions';
  // Legacy persisted group name from before Walk-ins / Systems merged into Actions
  if ((logGroups.Systems || []).includes(log.type)) return 'Actions';
  const found = Object.entries(logGroups).find(([, v]) => v.includes(log.type))?.[0];
  return found || '';
};

/** Whether a log is an inventory-item sale/activity (vs type/department/other). */
export const isInventoryItemActivity = (
  log: LogMatchable,
  inventoryItemNames?: Set<string> | string[],
): boolean => {
  if (isDepartmentEntryType(log.type)) return false;
  const itemSet = inventoryItemNames instanceof Set
    ? inventoryItemNames
    : new Set(inventoryItemNames || []);
  return itemSet.has(log.type)
    || Boolean(log.inventory_item_id)
    || (log.units != null && Boolean(log.inventory_type));
};

/** Groups the /logs table uses to tell departments from inventory / entries. */
export type ActivityFilterGroups = {
  Departments?: string[],
  Inventory?: string[],
  'Inventory Group'?: string[],
  'Department entries'?: string[],
};

const lowerActivityKey = (value?: string | null) => (value || '').trim().toLowerCase();

const toLowerActivitySet = (names: string[]) => new Set(
  names.map((n) => n.trim().toLowerCase()).filter(Boolean),
);

const activityNamesInclude = (names: string[] | undefined, value?: string | null) => {
  const key = lowerActivityKey(value);
  if (!key) return false;
  return (names || []).some((n) => lowerActivityKey(n) === key);
};

/** Split checked activityLogs into department / inventory / department-entry buckets. */
export const splitSelectedActivityLogs = (
  activityLogs: string[] | null | undefined,
  logGroups: ActivityFilterGroups,
) => {
  const selected = toLowerActivitySet(activityLogs || []);
  const hasShowAll = selected.has('show all');
  const deptNames = logGroups.Departments || [];
  const itemNames = logGroups.Inventory || [];
  const groupNames = logGroups['Inventory Group'] || [];
  const entryNames = (logGroups['Department entries'] && logGroups['Department entries'].length)
    ? logGroups['Department entries']
    : [...DEPARTMENT_ENTRY_TYPES];

  const selectedDepartments = (hasShowAll || selected.has('departments'))
    ? deptNames
    : deptNames.filter((n) => selected.has(lowerActivityKey(n)));
  const selectedInventoryItems = (hasShowAll || selected.has('inventory'))
    ? itemNames
    : itemNames.filter((n) => selected.has(lowerActivityKey(n)));
  const selectedInventoryGroups = (hasShowAll || selected.has('inventory group'))
    ? groupNames
    : groupNames.filter((n) => selected.has(lowerActivityKey(n)));
  const selectedDepartmentEntries = (
    hasShowAll || selected.has(lowerActivityKey(DEPARTMENT_ENTRY_GROUP))
  )
    ? entryNames
    : entryNames.filter((n) => selected.has(lowerActivityKey(n)));

  return {
    selectedDepartments,
    selectedInventoryItems,
    selectedInventoryGroups,
    selectedDepartmentEntries,
    hasDepartmentSelection: selectedDepartments.length > 0,
    hasInventoryItemSelection: selectedInventoryItems.length > 0,
    hasInventoryGroupSelection: selectedInventoryGroups.length > 0,
    hasInventorySelection:
      selectedInventoryItems.length > 0 || selectedInventoryGroups.length > 0,
    hasDepartmentEntrySelection: selectedDepartmentEntries.length > 0,
  };
};

/** Inventory can show on /logs with no department selected. */
export const activityLogsNeedDepartmentToViewInventory = (
  _activityLogs: string[],
  _logGroups: ActivityFilterGroups,
): boolean => false;

/** Direct / Expense / Document can show on /logs with no department selected. */
export const activityLogsNeedDepartmentToViewEntries = (
  _activityLogs: string[],
  _logGroups: ActivityFilterGroups,
): boolean => false;

/** Department selected with no inventory — department activityLogs stay hidden. */
export const activityLogsNeedInventoryToViewDepartment = (
  activityLogs: string[],
  logGroups: ActivityFilterGroups,
): boolean => {
  const split = splitSelectedActivityLogs(activityLogs, logGroups);
  return split.hasDepartmentSelection
    && !split.hasInventorySelection
    && !split.hasDepartmentEntrySelection;
};

/** Empty /logs hint when a department is selected with no inventory or entries. */
export const activityLogsBoundSelectionHint = (
  activityLogs: string[],
  logGroups: ActivityFilterGroups,
): string | null => {
  if (activityLogsNeedInventoryToViewDepartment(activityLogs, logGroups)) {
    return 'Select inventory to view departments';
  }
  return null;
};

/** Sale / stock inventory rows (not Inventory Added lifecycle events). */
export const isInventoryFlowLog = (
  log: LogMatchable,
  logGroups?: ActivityFilterGroups,
): boolean => {
  if (isInventorySystemType(log.type)) return false;
  if (isDepartmentEntryType(log.type)) return false;
  if (isInventoryItemActivity(log, logGroups?.Inventory)) return true;
  if (log.inventory_type) return true;
  if (activityNamesInclude(logGroups?.['Inventory Group'], log.type)) return true;
  return false;
};

/**
 * /logs table match.
 * Dynamic model: Show all | activity-scope id | any log.skus[].id in checked.
 */
export const logMatchesActivityLogsTable = (
  log: LogMatchable,
  activityLogs: string[],
  logGroups: ActivityFilterGroups,
  activityGroupsModel?: ActivityGroupsModel | null,
): boolean => {
  if (!activityLogs?.length) return false;
  if (isHiddenActivityLogsActionType(log.type)) return false;

  if (activityGroupsModel && activityGroupsModel.groups.length > 0) {
    return logMatchesActivityGroupsSelection(
      log,
      activityLogs,
      activityGroupsModel,
      null,
    );
  }

  // checked is UUID / SKU-id after migrate — without a groups model, hide until loaded.
  const looksLikeNewSelection = activityLogs.some(
    (a) => a === SHOW_ALL_ACTIVITY_LOGS || isActivityGroupUuid(a) || isActivitySkuSelectionId(a),
  );
  if (looksLikeNewSelection) {
    return false;
  }

  const split = splitSelectedActivityLogs(activityLogs, logGroups);
  const deptSet = toLowerActivitySet(split.selectedDepartments);
  const itemSet = toLowerActivitySet(split.selectedInventoryItems);
  const groupSet = toLowerActivitySet(split.selectedInventoryGroups);
  const entrySet = toLowerActivitySet(split.selectedDepartmentEntries);
  const logDept = lowerActivityKey(log.department);
  const logGroup = lowerActivityKey(log.inventory_type);
  const logType = lowerActivityKey(log.type);

  // Direct / Expense / Document: no department → all of that type; department on → that department only.
  if (isDepartmentEntryType(log.type)) {
    if (!split.hasDepartmentEntrySelection) return false;
    if (split.hasDepartmentSelection) {
      if (!logDept || !deptSet.has(logDept)) return false;
    }
    return entrySet.has(logType);
  }

  if (isInventoryFlowLog(log, logGroups)) {
    if (!split.hasInventorySelection) return false;
    // Department on: only that department's selected inventory. Off: every department.
    if (split.hasDepartmentSelection) {
      if (!logDept || !deptSet.has(logDept)) return false;
    }

    const itemMatch = itemSet.has(logType);
    const groupMatch = groupSet.has(logGroup) || groupSet.has(logType);
    // Group-named log (type is the group), same role as a department-named log.
    const isGroupNamedLog = groupSet.has(logType);

    // Group acts like department for items: both on → item must belong to the group.
    if (split.hasInventoryItemSelection && split.hasInventoryGroupSelection) {
      if (isGroupNamedLog) return true;
      return itemMatch && groupMatch;
    }
    if (split.hasInventoryItemSelection) return itemMatch;
    return groupMatch;
  }

  // Legacy department activityLogs (type = department name) need inventory selected too.
  if (activityNamesInclude(logGroups.Departments, log.type)) {
    if (!split.hasDepartmentSelection || !split.hasInventorySelection) return false;
    return deptSet.has(logType);
  }

  return activityLogs.some((a) => logMatchesActivity(a, log));
};

/** Filter /logs table rows with department/group-scoped inventory matching. */
export const filterLogsByActivityLogsTable = <T extends LogMatchable>(
  logs: T[],
  activityLogs: string[],
  logGroups: ActivityFilterGroups,
  activityGroupsModel?: ActivityGroupsModel | null,
): T[] => {
  if (!activityLogs?.length) return [];
  const matched = logs.filter((log) => (
    logMatchesActivityLogsTable(log, activityLogs, logGroups, activityGroupsModel)
  ));
  return dedupeLogsById(matched);
};

/**
 * How temporary /logs type labels attribute inventory (and department-entry)
 * rows when a department or inventory group is selected.
 * - department / inventory_group: rollup labels
 * - sku: actual log type (stock-keeping unit / entry type)
 * Legacy `rollup` is treated as department when normalizing.
 */
export type ActivityLogsTypeAttributeMode = 'department' | 'inventory_group' | 'sku';

/** True when the Activity-column attribute control should appear. */
export const activityLogsTypeAttributeControlVisible = (
  activityLogs: string[],
  logGroups: ActivityFilterGroups,
): boolean => {
  const split = splitSelectedActivityLogs(activityLogs, logGroups);
  return split.hasDepartmentSelection || split.hasInventoryGroupSelection;
};

/** Clamp stored mode to what the current department / group selection allows. */
export const normalizeActivityLogsTypeAttributeMode = (
  mode: string | null | undefined,
  activityLogs: string[],
  logGroups: ActivityFilterGroups,
): ActivityLogsTypeAttributeMode => {
  const split = splitSelectedActivityLogs(activityLogs, logGroups);
  if (mode === 'sku') return 'sku';
  if (mode === 'inventory_group' && split.hasInventoryGroupSelection) {
    return 'inventory_group';
  }
  if (
    (mode === 'department' || mode === 'rollup')
    && split.hasDepartmentSelection
  ) {
    return 'department';
  }
  if (split.hasDepartmentSelection) return 'department';
  if (split.hasInventoryGroupSelection) return 'inventory_group';
  return 'sku';
};

/** Which Attribute-by options to show for the current ActivityLogs selection. */
export const activityLogsTypeAttributeOptionKeys = (
  activityLogs: string[],
  logGroups: ActivityFilterGroups,
): ActivityLogsTypeAttributeMode[] => {
  const split = splitSelectedActivityLogs(activityLogs, logGroups);
  const keys: ActivityLogsTypeAttributeMode[] = [];
  if (split.hasDepartmentSelection) keys.push('department');
  // Show inventory group whenever it is selected (including alongside department).
  if (split.hasInventoryGroupSelection) keys.push('inventory_group');
  keys.push('sku');
  return keys;
};

/**
 * /logs type column: activity-scope string when using the dynamic model;
 * else legacy department / inventory_group / sku attribute.
 */
export const activityLogsTableTypeName = (
  log: LogMatchable,
  activityLogs: string[],
  logGroups: ActivityFilterGroups,
  attributeMode: ActivityLogsTypeAttributeMode | 'rollup' = 'department',
  activityGroupsModel?: ActivityGroupsModel | null,
): string => {
  // Legacy Low-level * rows still show the Compliance * Action name.
  const rawType = String(log.type || '');
  if (rawType === 'Low-level favorable') return 'Compliance favorable';
  if (rawType === 'Low-level note') return 'Compliance note';
  if (rawType === 'Low-level incident' || rawType === 'Low-level problem') {
    return 'Compliance incident';
  }
  if (rawType === 'Low-level alert') return 'Compliance alert';

  if (activityGroupsModel && activityGroupsModel.groups.length > 0) {
    return activityLogsTableGroupTypeName(log);
  }

  const mode = normalizeActivityLogsTypeAttributeMode(attributeMode, activityLogs, logGroups);
  // SKU mode: always the real activity type on the log (item / Direct / etc.).
  if (mode === 'sku') return log.type;

  const split = splitSelectedActivityLogs(activityLogs, logGroups);
  const dept = (log.department || '').trim();
  const group = (log.inventory_type || '').trim();
  const deptSet = toLowerActivitySet(split.selectedDepartments);
  const groupSet = toLowerActivitySet(split.selectedInventoryGroups);

  if (mode === 'inventory_group') {
    if (!isInventoryFlowLog(log, logGroups)) return log.type;
    if (group && groupSet.has(lowerActivityKey(group))) return group;
    if (groupSet.has(lowerActivityKey(log.type))) return log.type;
    return log.type;
  }

  // Department attribute mode.
  if (isDepartmentEntryType(log.type)) {
    if (dept && deptSet.has(lowerActivityKey(dept))) return dept;
    return log.type;
  }

  if (!isInventoryFlowLog(log, logGroups)) return log.type;

  if (split.hasDepartmentSelection && dept && deptSet.has(lowerActivityKey(dept))) {
    return dept;
  }
  return log.type;
};

/**
 * Checked list so /logs can show inventory and department entries for the
 * given departments (department plus matching types).
 */
export const departmentInventoryActivityLogsChecked = (
  departmentNames: string[],
  logGroups: ActivityFilterGroups,
): string[] => {
  const depts = departmentNames.map((n) => n.trim()).filter(Boolean);
  const entryTypes = (logGroups['Department entries'] && logGroups['Department entries'].length)
    ? logGroups['Department entries']
    : [...DEPARTMENT_ENTRY_TYPES];
  return Array.from(new Set([
    'Departments',
    ...depts,
    'Inventory',
    ...(logGroups.Inventory || []),
    'Inventory Group',
    ...(logGroups['Inventory Group'] || []),
    DEPARTMENT_ENTRY_GROUP,
    ...entryTypes,
  ]));
};

/** Keep a newly added log visible: select its activity scope / skus / legacy type fields. */
export const activityLogsCheckedToRevealLog = (
  checked: string[],
  log: LogMatchable,
  activityGroupsModel?: ActivityGroupsModel | null,
): string[] => {
  if (activityGroupsModel && activityGroupsModel.groups.length > 0) {
    return activityLogsCheckedToRevealLogSelection(checked, log, activityGroupsModel);
  }
  const extras = [log.type, log.departments || log.department, log.inventory_type]
    .map((s) => (s || '').trim())
    .filter(Boolean);
  if (isDepartmentEntryType(log.type)) extras.push(DEPARTMENT_ENTRY_GROUP);
  const extraSet = new Set(extras);
  return [...checked.filter((c) => !extraSet.has(c)), ...extras];
};

/** How inventory cashflow is grouped in dashboard flow cards. */
export type CashflowInventoryMode = 'department' | 'inventory_type' | 'inventory_item';

export const CASHFLOW_INVENTORY_OPTIONS: { value: CashflowInventoryMode; label: string }[] = [
  { value: 'department', label: 'Department' },
  { value: 'inventory_type', label: 'Inventory Type' },
  { value: 'inventory_item', label: 'Inventory Item' },
];

export const cashflowModeLabel = (mode: CashflowInventoryMode | string | null | undefined) => (
  CASHFLOW_INVENTORY_OPTIONS.find((o) => o.value === mode)?.label || 'Inventory Item'
);

export const cashflowModeHint: Record<CashflowInventoryMode, string> = {
  department: 'Inventory cashflow by department',
  inventory_type: 'Inventory cashflow by inventory type',
  inventory_item: 'Inventory cashflow by inventory item',
};

/** Cashflow display name for a log under an inventory grouping mode. */
export const cashflowBucketName = (
  log: LogMatchable,
  inventoryMode?: CashflowInventoryMode | string | null,
  inventoryItemNames?: Set<string> | string[],
): string => {
  if (isDepartmentEntryType(log.type)) return log.type;
  const isItemActivity = isInventoryItemActivity(log, inventoryItemNames);
  const isInventoryFlow = isItemActivity || Boolean(log.inventory_type);

  // Inventory cashflow only — other log types keep their activity name.
  if (isInventoryFlow) {
    if (inventoryMode === 'department') {
      return log.department || log.inventory_type || log.type;
    }
    if (inventoryMode === 'inventory_type') {
      return log.inventory_type || log.type;
    }
    // inventory_item (default): item sales by activity name, else by type
    if (isItemActivity) return log.type;
    return log.inventory_type || log.type;
  }

  return log.type;
};

/** Metrics that resolve inventory by selected activity priority (item > group > department). */
export const INVENTORY_HIERARCHY_METRICS = [
  'Active audits',
  'Active audits2',
  'All balances',
  'All audits',
] as const;

const selectedHas = (selected: Set<string>, name?: string | null) => {
  const trimmed = name?.trim();
  if (!trimmed) return false;
  return selected.has(trimmed.toLowerCase());
};

/**
 * For mixed activity selections: prefer inventory item, else group, else leftover department.
 * Parent keys (Inventory / Inventory Group) count as that whole level.
 * A selected department only scopes which inventory is included — charts still
 * show items/groups, not the department name (and not department entries).
 */
export const resolveInventorySelectionLevel = (
  log: LogMatchable,
  selectedActivityLogs?: string[] | null,
  inventoryItemNames?: Set<string> | string[],
): CashflowInventoryMode | null => {
  if (isDepartmentEntryType(log.type)) return null;
  const isItemActivity = isInventoryItemActivity(log, inventoryItemNames);
  const isInventoryFlow = isItemActivity || Boolean(log.inventory_type);
  if (!isInventoryFlow) return null;

  const selected = new Set(
    (selectedActivityLogs || []).map((s) => s.trim().toLowerCase()).filter(Boolean),
  );
  const itemName = (log.type || '').trim();
  const groupName = (log.inventory_type || '').trim();
  const deptName = (log.department || '').trim();

  if (isItemActivity && (selectedHas(selected, itemName) || selected.has('inventory'))) {
    return 'inventory_item';
  }
  if (groupName && (selectedHas(selected, groupName) || selected.has('inventory group'))) {
    return 'inventory_type';
  }

  // Included without an explicit item/group selection (e.g. department only) —
  // keep the most specific available label so Active activityLogs shows inventory.
  if (isItemActivity) return 'inventory_item';
  if (groupName) return 'inventory_type';
  if (deptName) return 'department';
  return null;
};

/**
 * Active-activityLogs attribute mode → cashflow level (matches /logs type column).
 * Only applies when a department or inventory group is selected.
 */
export const activityLogsAttributeModeToCashflowLevel = (
  selectedActivityLogs: string[] | null | undefined,
  logGroups: ActivityFilterGroups | Record<string, string[]>,
  attributeMode: ActivityLogsTypeAttributeMode | 'rollup' = 'department',
): CashflowInventoryMode | null => {
  const split = splitSelectedActivityLogs(selectedActivityLogs, logGroups as ActivityFilterGroups);
  if (!split.hasDepartmentSelection && !split.hasInventoryGroupSelection) return null;
  const mode = normalizeActivityLogsTypeAttributeMode(
    attributeMode,
    selectedActivityLogs || [],
    logGroups as ActivityFilterGroups,
  );
  if (mode === 'sku') return 'inventory_item';
  if (mode === 'inventory_group') return 'inventory_type';
  if (mode === 'department') return 'department';
  return null;
};

/** Primary log-group ring for hierarchy metrics (item → Inventory, group → Inventory Group, etc.). */
export const getMetricsPrimaryLogGroup = (
  log: LogMatchable,
  logGroups: Record<string, string[]>,
  metricName?: string,
  selectedActivityLogs?: string[] | null,
  inventoryItemNames?: Set<string> | string[],
  attributeMode?: ActivityLogsTypeAttributeMode | null,
): string => {
  if ((INVENTORY_HIERARCHY_METRICS as readonly string[]).includes(metricName || '')) {
    if (
      (metricName === 'Active audits' || metricName === 'Active audits2')
      && attributeMode
    ) {
      const forced = activityLogsAttributeModeToCashflowLevel(
        selectedActivityLogs,
        logGroups,
        attributeMode,
      );
      if (forced === 'inventory_item') return 'Inventory';
      if (forced === 'inventory_type') return 'Inventory Group';
      if (forced === 'department') return 'Departments';
    }
    const level = resolveInventorySelectionLevel(log, selectedActivityLogs, inventoryItemNames);
    if (level === 'inventory_item') return 'Inventory';
    if (level === 'inventory_type') return 'Inventory Group';
    if (level === 'department') return 'Departments';
  }
  return getPrimaryLogGroup(log, logGroups);
};

/**
 * Series / slice name for Metrics charts.
 * Departments tab attributes inventory sales to the department; Direct /
 * Expense / Document stay their own series among those department slices.
 * Active activityLogs / All balances / All activityLogs: item if selected, else group,
 * else most specific available (department selection still shows inventory).
 * When `attributeMode` is set for Active activityLogs, matches the /logs type column.
 */
export const metricsBucketName = (
  log: LogMatchable,
  metricName?: string,
  inventoryMode?: CashflowInventoryMode | string | null,
  inventoryItemNames?: Set<string> | string[],
  selectedActivityLogs?: string[] | null,
  staffMaps?: StaffMaps | null,
  attributeMode?: ActivityLogsTypeAttributeMode | null,
  logGroups?: ActivityFilterGroups | Record<string, string[]> | null,
): string => {
  // Counts → Users: each chart series is a staff member, not an activity type.
  if (metricName === 'Employees') {
    const identity = getLogActorIdentity(log);
    if (!staffMaps) return identity || 'staff';
    return resolveActorDisplayName(identity, staffMaps);
  }
  if (inventoryMode) {
    return cashflowBucketName(log, inventoryMode, inventoryItemNames);
  }
  if (metricName === 'Departments') {
    if (isDepartmentEntryType(log.type)) return log.type;
    return (log.department || log.type || '').trim();
  }
  if (metricName === 'Inventory') {
    return cashflowBucketName(log, 'inventory_item', inventoryItemNames);
  }
  if ((INVENTORY_HIERARCHY_METRICS as readonly string[]).includes(metricName || '')) {
    // Keep Active activityLogs chart labels in sync with the /logs attribute control.
    if (
      (metricName === 'Active audits' || metricName === 'Active audits2')
      && attributeMode
      && logGroups
    ) {
      const forced = activityLogsAttributeModeToCashflowLevel(
        selectedActivityLogs,
        logGroups,
        attributeMode,
      );
      if (forced) return cashflowBucketName(log, forced, inventoryItemNames);
    }
    const level = resolveInventorySelectionLevel(log, selectedActivityLogs, inventoryItemNames);
    if (level) {
      return cashflowBucketName(log, level, inventoryItemNames);
    }
  }
  return cashflowBucketName(log, null, inventoryItemNames);
};

/** Cross-tab jump payload so Compliance can open ActivityLogs with filters applied. */
const LOGS_JUMP_KEY = 'lodgefirst_logs_jump';

export type LogsJumpFilter = {
  search: string;
  /** When true, exclude activityLogs matching `search` (negative search). */
  searchNegate?: boolean;
  /** Exact tagged match mode for jump search. */
  searchMatchMode?: 'any' | 'room' | 'staff' | 'customer' | 'department' | 'ota';
  /** Full-term equality (no substring) for jump search. */
  searchExactMatch?: boolean;
  /** Skip archived inventory items, groups, and deleted departments. */
  hideArchives?: boolean;
  checked: string[];
  startDate?: string;
  endDate?: string;
  /** Report jump: first / last activity_logs id in the clicked set. */
  startLogId?: string;
  endLogId?: string;
  /** Unlock Associate Manager visibility of higher-role activityLogs (Compliance). */
  elevate?: boolean;
  /**
   * Compliance associated-activityLogs mode: after the compliance cashier's first entry,
   * hide other users. Cleared when time range is turned off or changed.
   */
  isComplianceActivity?: boolean;
  /** Compliance cashier staff id (preferred). */
  cashierId?: string;
  /** Compliance cashier username (fallback). */
  cashierUsername?: string;
  /** Value column dropdown: money vs inventory units. */
  valueMode?: 'value' | 'units';
  /** Scroll/highlight this activity id after filters apply. */
  focusLogId?: string;
  /**
   * Staff-report Show logs: temporarily view ActivityLogs as the other party
   * (permission-filtered set). Never elevates above the signed-in user.
   */
  viewAsPermission?: number;
  viewAsStaffId?: string;
  viewAsUsername?: string;
  viewAsLabel?: string;
  /** ActivityLogs Combine SKUs toggle at jump time. */
  combineSku?: boolean;
  /** /logs Activity-header attribute of the clicked logs. */
  typeAttributeMode?: ActivityLogsTypeAttributeMode;
  /** Exact activity_logs ids from a report chip / Total click. */
  onlyLogIds?: string[];
  ts: number;
};

/**
 * Cash sessions pin when a figure has no rows (opening ₦0).
 * Must stay non-empty — `onlyLogIds: []` was treated as unpinned.
 */
export const CASH_SESSIONS_EMPTY_ONLY_LOG_ID = '__cash_sessions_empty__';

/** Parse stored / jump Activity-header attribute. Unknown → leave unset. */
export const parseActivityLogsTypeAttributeModeParam = (
  raw: string | null | undefined,
): ActivityLogsTypeAttributeMode | undefined => {
  if (raw === 'sku' || raw === 'inventory_group' || raw === 'department') return raw;
  // Legacy rollup → department.
  if (raw === 'rollup') return 'department';
  return undefined;
};

export const buildLogsJumpHref = (filter: Omit<LogsJumpFilter, 'ts'>) => {
  const params = new URLSearchParams();
  if (filter.search) params.set('q', filter.search);
  if (filter.searchNegate) params.set('negate', '1');
  if (filter.searchMatchMode && filter.searchMatchMode !== 'any') {
    params.set('match', filter.searchMatchMode);
  }
  if (filter.searchExactMatch) params.set('exact', '1');
  if (filter.hideArchives) params.set('hideArchives', '1');
  params.set('activityLogs', filter.checked.join(','));
  if (filter.startDate) params.set('from', filter.startDate);
  if (filter.endDate) params.set('to', filter.endDate);
  if (filter.startLogId) params.set('fromId', String(filter.startLogId));
  if (filter.endLogId) params.set('toId', String(filter.endLogId));
  if (filter.elevate) params.set('elevate', '1');
  if (filter.isComplianceActivity) params.set('compliance', '1');
  if (filter.cashierId) params.set('cashierId', filter.cashierId);
  if (filter.cashierUsername) params.set('cashierName', filter.cashierUsername);
  if (filter.valueMode === 'units') params.set('value', 'units');
  if (filter.focusLogId) params.set('focus', String(filter.focusLogId));
  if (filter.viewAsPermission != null && Number.isFinite(Number(filter.viewAsPermission))) {
    params.set('viewAsPerm', String(Math.floor(Number(filter.viewAsPermission))));
  }
  if (filter.viewAsStaffId) params.set('viewAsStaff', String(filter.viewAsStaffId));
  if (filter.viewAsUsername) params.set('viewAsUser', String(filter.viewAsUsername));
  if (filter.viewAsLabel) params.set('viewAsLabel', String(filter.viewAsLabel).slice(0, 80));
  if (filter.combineSku) params.set('combineSku', '1');
  if (filter.typeAttributeMode === 'sku'
    || filter.typeAttributeMode === 'inventory_group'
    || filter.typeAttributeMode === 'department') {
    params.set('attr', filter.typeAttributeMode);
  }
  // null/omit = no pin; non-empty list (incl. cash-sessions empty sentinel) pins exact ids.
  if (filter.onlyLogIds?.length) {
    params.set(
      'only',
      [...new Set(filter.onlyLogIds.map((id) => String(id || '').trim()).filter(Boolean))].join(','),
    );
  }
  return `${ERP_PATHS.activityLogsAction(0)}?${params.toString()}`;
};

export const parseLogsJumpFromSearch = (
  search: string,
): Omit<LogsJumpFilter, 'ts'> | null => {
  const params = new URLSearchParams(
    search.startsWith('?') ? search.slice(1) : search,
  );
  const activityLogs = params.get('activityLogs') || params.get('audits');
  const from = params.get('from');
  const to = params.get('to');
  const startLogId = params.get('fromId')?.trim() || undefined;
  const endLogId = params.get('toId')?.trim() || undefined;
  if (!activityLogs) return null;
  if (!startLogId && !endLogId && (!from || !to)) return null;
  const elevate = params.get('elevate') === '1';
  const complianceParam = params.get('compliance') === '1';
  const cashierId = params.get('cashierId')?.trim() || undefined;
  const cashierUsername = params.get('cashierName')?.trim() || undefined;
  const matchRaw = params.get('match');
  const searchMatchMode = matchRaw === 'room' || matchRaw === 'staff'
    || matchRaw === 'customer' || matchRaw === 'department' || matchRaw === 'ota'
    ? matchRaw
    : 'any';
  const focusLogId = params.get('focus')?.trim() || undefined;
  const viewAsPermRaw = params.get('viewAsPerm');
  const viewAsPermission = viewAsPermRaw != null && viewAsPermRaw !== ''
    && Number.isFinite(Number(viewAsPermRaw))
    ? Math.floor(Number(viewAsPermRaw))
    : undefined;
  return {
    search: params.get('q') || '',
    searchNegate: params.get('negate') === '1',
    searchMatchMode,
    searchExactMatch: params.get('exact') === '1',
    hideArchives: params.get('hideArchives') === '1'
      || params.get('hideTypes') === '1'
      || params.get('hideGroups') === '1'
      || params.get('hideDepts') === '1',
    checked: activityLogs.split(',').map((a) => a.trim()).filter(Boolean),
    startDate: from || undefined,
    endDate: to || undefined,
    startLogId,
    endLogId,
    elevate,
    // Cash sessions send elevate without compliance (opening float). Do not treat elevate as compliance.
    isComplianceActivity: complianceParam,
    cashierId,
    cashierUsername,
    valueMode: params.get('value') === 'units' ? 'units' : 'value',
    focusLogId,
    viewAsPermission,
    viewAsStaffId: params.get('viewAsStaff')?.trim() || undefined,
    viewAsUsername: params.get('viewAsUser')?.trim() || undefined,
    viewAsLabel: params.get('viewAsLabel')?.trim() || undefined,
    combineSku: params.get('combineSku') === '1',
    typeAttributeMode: parseActivityLogsTypeAttributeModeParam(params.get('attr')),
    onlyLogIds: (() => {
      const raw = params.get('only');
      if (raw == null || raw === '_none_' || raw.trim() === '') return undefined;
      const ids = raw
        .split(',')
        .map((id) => id.trim())
        .filter(Boolean);
      return ids.length ? ids : undefined;
    })(),
  };
};

export const peekLogsJump = (): LogsJumpFilter | null => {
  try {
    const raw = localStorage.getItem(LOGS_JUMP_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as LogsJumpFilter;
    if (!parsed?.ts || Date.now() - parsed.ts > 30_000) return null;
    return parsed;
  } catch {
    return null;
  }
};

export const stashLogsJump = (filter: Omit<LogsJumpFilter, 'ts'>) => {
  localStorage.setItem(LOGS_JUMP_KEY, JSON.stringify({ ...filter, ts: Date.now() }));
};

export const consumeLogsJump = (): LogsJumpFilter | null => {
  try {
    const raw = localStorage.getItem(LOGS_JUMP_KEY);
    if (!raw) return null;
    localStorage.removeItem(LOGS_JUMP_KEY);
    const parsed = JSON.parse(raw) as LogsJumpFilter;
    if (!parsed?.ts || Date.now() - parsed.ts > 30_000) return null;
    return parsed;
  } catch {
    return null;
  }
};
